import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu, X, ArrowRight, ChevronDown, Brain, Code2, Globe, Smartphone,
  Layers, Cloud, Boxes, Contact2, Users2, Radio, Workflow, ShieldCheck, Cpu, Rocket, Compass
} from "lucide-react";
import { Magnetic } from "./motion";
import { Logo } from "./Logo";
import { openConsultationModal } from "./ConsultationModal";

const SERVICES_DROPDOWN = [
  { label: "Software Development", to: "/services/software-development", icon: Code2, desc: "Web, mobile & enterprise apps" },
  { label: "AI Solutions", to: "/services/ai-solutions", icon: Brain, desc: "Cognitive models, LLMs & autonomous agents" },
  { label: "IoT & Automation", to: "/services/iot-solutions", icon: Radio, desc: "Hardware telemetry, sensors & edge systems" },
  { label: "Cybersecurity (Guardian)", to: "/guardian", icon: ShieldCheck, desc: "Autonomous threat defense & zero-trust SRE" },
  { label: "Cloud Infrastructure", to: "/services/cloud-infrastructure", icon: Cloud, desc: "Dedicated extreme-speed bare metal on neuravolt.cloud" },
  { label: "Technology Consultancy", to: "/services/consultancy", icon: Compass, desc: "Digital transformation & strategic advisory" },
  { label: "nORBIT Enterprise OS", to: "/norbit", icon: Cpu, desc: "One Platform. Your Entire Business." },
  { label: "Reach Growth Engine", to: "/reach", icon: Rocket, desc: "Automated customer acquisition & growth engine" },
];

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", hasDropdown: true },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/portfolio" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const [scrollDirection, setScrollDirection] = useState("up"); // "up" | "down"
  const [activeGuardianSection, setActiveGuardianSection] = useState("overview");
  const [activeNorbitSection, setActiveNorbitSection] = useState("overview");
  const [mobileTab, setMobileTab] = useState("main");
  const lastScrollYRef = useRef(0);
  const location = useLocation();

  const isGuardianPage = location.pathname.startsWith("/guardian") || location.pathname === "/services/cybersecurity";
  const isNorbitPage = location.pathname.startsWith("/norbit");

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
    if (isGuardianPage) setMobileTab("guardian");
    else if (isNorbitPage) setMobileTab("norbit");
    else setMobileTab("main");
  }, [location.pathname, isGuardianPage, isNorbitPage]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handle = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 20);

      const diff = currentScrollY - lastScrollYRef.current;
      if (Math.abs(diff) > 10) {
        if (diff > 0 && currentScrollY > 120) {
          setScrollDirection("down");
        } else if (diff < 0 || currentScrollY <= 80) {
          setScrollDirection("up");
        }
        lastScrollYRef.current = currentScrollY;
      }

      // Track active section on Guardian page
      if (isGuardianPage) {
        const sections = [
          "overview",
          "how-it-works",
          "capabilities",
          "architecture",
          "use-cases",
          "deployments",
          "faq",
        ];
        for (const secId of sections) {
          const el = document.getElementById(secId);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 260 && rect.bottom >= 120) {
              setActiveGuardianSection(secId);
              break;
            }
          }
        }
      }

      // Track active section on Norbit page
      if (isNorbitPage) {
        const norbitSections = [
          "overview",
          "philosophy",
          "five-pillars",
          "workflows",
          "savings",
          "deployments",
        ];
        for (const secId of norbitSections) {
          const el = document.getElementById(secId);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 260 && rect.bottom >= 120) {
              setActiveNorbitSection(secId);
              break;
            }
          }
        }
      }

      setOpen(false);
      // Only close dropdown on significant intentional scroll, not on inertia micro-scrolls
      if (Math.abs(diff) > 25) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, [isGuardianPage, isNorbitPage]);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 260);
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isLightHero = 
    location.pathname.startsWith("/norbit") || 
    location.pathname.startsWith("/guardian") || 
    location.pathname.startsWith("/reach") || 
    location.pathname === "/services/cybersecurity" || 
    location.pathname === "/404";
  const onDark = !isLightHero;

  const showGuardianNav = isGuardianPage && scrollDirection === "down" && scrolled;
  const showNorbitNav = isNorbitPage && scrollDirection === "down" && scrolled;

  const GUARDIAN_NAV = [
    { label: "Overview", to: "#overview", id: "overview" },
    { label: "How It Works", to: "#how-it-works", id: "how-it-works" },
    { label: "Capabilities", to: "#capabilities", id: "capabilities" },
    { label: "Architecture", to: "#architecture", id: "architecture" },
    { label: "Use Cases", to: "#use-cases", id: "use-cases" },
    { label: "Deployments", to: "#deployments", id: "deployments" },
    { label: "FAQ", to: "#faq", id: "faq" },
  ];

  const NORBIT_NAV = [
    { label: "Overview", to: "#overview", id: "overview" },
    { label: "Philosophy", to: "#philosophy", id: "philosophy" },
    { label: "5 Engines", to: "#five-pillars", id: "five-pillars" },
    { label: "Workflows", to: "#workflows", id: "workflows" },
    { label: "Cost Savings", to: "#savings", id: "savings" },
    { label: "Deployments", to: "#deployments", id: "deployments" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-8 py-3 sm:py-5 transition-all duration-300 pointer-events-none">
      <div className={`max-w-7xl mx-auto transition-all duration-300 pointer-events-auto ${scrolled ? "glass-card rounded-full px-4 sm:px-5 py-2 sm:py-2.5 shadow-2xl" : ""}`}>
        <div className="flex items-center justify-between gap-3">
          
          {/* Always show official NeuraVolt Logo in Navbar */}
          <Logo dark={onDark && !scrolled} />

          {/* Desktop Nav Section */}
          {showGuardianNav ? (
            <nav data-testid="guardian-desktop-nav" className={`hidden lg:flex items-center gap-1.5 p-1 rounded-full ${scrolled ? "bg-black/5" : "bg-[#0B0B0B]/5"} transition-all duration-300 animate-in fade-in slide-in-from-top-1`}>
              {GUARDIAN_NAV.map((item) => {
                const isSecActive = activeGuardianSection === item.id;
                return (
                  <a
                    key={item.label}
                    href={item.to}
                    className={`px-3.5 py-1.5 text-xs font-medium transition-colors rounded-full hover:bg-black/5 relative ${
                      isSecActive
                        ? "text-[#0B0B0B] font-semibold after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-[#FF5A1F]"
                        : "text-neutral-700 hover:text-[#0B0B0B]"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>
          ) : showNorbitNav ? (
            <nav data-testid="norbit-desktop-nav" className={`hidden lg:flex items-center gap-1.5 p-1 rounded-full ${scrolled ? "bg-black/5" : "bg-[#0B0B0B]/5"} transition-all duration-300 animate-in fade-in slide-in-from-top-1`}>
              {NORBIT_NAV.map((item) => {
                const isSecActive = activeNorbitSection === item.id;
                return (
                  <a
                    key={item.label}
                    href={item.to}
                    className={`px-3.5 py-1.5 text-xs font-medium transition-colors rounded-full hover:bg-black/5 relative ${
                      isSecActive
                        ? "text-[#0B0B0B] font-semibold after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-[#FF5A1F]"
                        : "text-neutral-700 hover:text-[#0B0B0B]"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>
          ) : (
            <nav data-testid="desktop-nav" className={`hidden lg:flex items-center gap-1 p-1 rounded-full ${scrolled ? "bg-black/5" : onDark ? "bg-white/10" : "bg-[#0B0B0B]/5"} transition-all duration-300 animate-in fade-in slide-in-from-top-1`}>
              {NAV.map((l) => {
                const active = isActive(l.to);

                if (l.hasDropdown) {
                  return (
                    <div
                      key={l.to}
                      ref={dropdownRef}
                      className="relative"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen((prev) => !prev);
                        }}
                        className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 relative ${
                          active
                            ? "text-[#FF5A1F] font-semibold"
                            : onDark && !scrolled
                            ? "text-neutral-300 hover:text-white"
                            : "text-neutral-700 hover:text-[#0B0B0B]"
                        }`}
                        aria-expanded={dropdownOpen}
                        aria-haspopup="true"
                      >
                        {active && <span className="absolute inset-0 rounded-full bg-[#FF5A1F]/12" />}
                        <span className="relative">{l.label}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? "rotate-180 text-[#FF5A1F]" : ""}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {dropdownOpen && (
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 w-[580px] z-[70] animate-in fade-in slide-in-from-top-2 duration-200 before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-4 before:pointer-events-auto"
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="rounded-2xl p-4 bg-[#0F1117]/95 backdrop-blur-2xl border border-white/15 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                            <div className="flex items-center justify-between px-2 pb-3 mb-2 border-b border-white/10">
                              <span className="text-[11px] font-bold uppercase tracking-widest text-[#FF5A1F]">
                                Capabilities & Solutions
                              </span>
                              <Link
                                to="/services"
                                onClick={() => setDropdownOpen(false)}
                                className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1 transition-colors"
                              >
                                All Services <ArrowRight className="w-3 h-3" />
                              </Link>
                            </div>

                            <div className="grid grid-cols-2 gap-1.5 max-h-[380px] overflow-y-auto pr-1">
                              {SERVICES_DROPDOWN.map((item) => {
                                const Icon = item.icon;
                                const content = (
                                  <>
                                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 grid place-items-center shrink-0 group-hover:border-[#FF5A1F]/50 group-hover:bg-[#FF5A1F]/15 transition-colors">
                                      <Icon className="w-4 h-4 text-[#FF5A1F]" />
                                    </div>
                                    <div>
                                      <div className="text-xs font-semibold text-white group-hover:text-[#FF5A1F] transition-colors leading-snug flex items-center gap-1.5">
                                        {item.label}
                                        {item.external && <span className="text-[9px] px-1 py-0.2 rounded bg-white/10 text-neutral-300">ext</span>}
                                      </div>
                                      <div className="text-[11px] text-neutral-400 line-clamp-1 leading-normal">
                                        {item.desc}
                                      </div>
                                    </div>
                                  </>
                                );

                                if (item.external) {
                                  return (
                                    <a
                                      key={item.to}
                                      href={item.to}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setDropdownOpen(false)}
                                      className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/10 transition-colors group"
                                    >
                                      {content}
                                    </a>
                                  );
                                }

                                return (
                                  <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setDropdownOpen(false)}
                                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/10 transition-colors group"
                                  >
                                    {content}
                                  </Link>
                                );
                              })}
                            </div>

                            <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between px-2 text-xs">
                              <span className="text-neutral-400">Need a bespoke enterprise solution?</span>
                              <button
                                onClick={() => {
                                  setDropdownOpen(false);
                                  openConsultationModal();
                                }}
                                className="text-[#FF5A1F] font-semibold hover:underline inline-flex items-center gap-1"
                              >
                                Talk to an Architect <ArrowRight className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 relative ${
                      active
                        ? "text-[#FF5A1F] font-semibold"
                        : onDark && !scrolled
                        ? "text-neutral-300 hover:text-white"
                        : "text-neutral-700 hover:text-[#0B0B0B]"
                    }`}
                  >
                    {active && <span className="absolute inset-0 rounded-full bg-[#FF5A1F]/12" />}
                    <span className="relative">{l.label}</span>
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Right Action Button */}
          <div className="hidden lg:flex items-center gap-3">
            {showGuardianNav ? (
              <Magnetic>
                <button
                  data-testid="guardian-nav-audit-btn"
                  onClick={() => openConsultationModal("Guardian Security Audit")}
                  className="group inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md shadow-[#FF5A1F]/25 hover:scale-[1.02] active:scale-[0.98] animate-in fade-in zoom-in-95"
                >
                  <span>Book a Security Audit</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Magnetic>
            ) : showNorbitNav ? (
              <Magnetic>
                <button
                  data-testid="norbit-nav-demo-btn"
                  onClick={() => openConsultationModal("nORBIT Enterprise OS Demo")}
                  className="group inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md shadow-[#FF5A1F]/25 hover:scale-[1.02] active:scale-[0.98] animate-in fade-in zoom-in-95"
                >
                  <span>Request nORBIT Access</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Magnetic>
            ) : (
              <Magnetic>
                <button
                  data-testid="nav-consult-btn"
                  onClick={() => openConsultationModal()}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-colors animate-in fade-in zoom-in-95"
                  style={{ boxShadow: "0 10px 30px -10px rgba(255,90,31,0.6)" }}
                >
                  Book a Free Consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Magnetic>
            )}
          </div>

          <button
            data-testid="mobile-menu-btn"
            className={`lg:hidden p-2 rounded-full transition-colors ${
              onDark && !scrolled ? "text-white hover:bg-white/10" : "text-[#0B0B0B] hover:bg-black/5"
            }`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm lg:hidden z-[-1]" onClick={() => setOpen(false)} />
            <div
              data-testid="mobile-menu"
              className="lg:hidden mt-2 glass-card rounded-2xl p-2.5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto shadow-2xl border border-white/20"
            >
              {isGuardianPage && (
                <div className="grid grid-cols-2 p-1 bg-black/5 rounded-xl mb-1.5 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setMobileTab("guardian")}
                    className={`py-2 rounded-lg text-center transition-all ${
                      mobileTab === "guardian"
                        ? "bg-[#FF5A1F] text-white shadow-sm"
                        : "text-neutral-700 hover:text-black"
                    }`}
                  >
                    Guardian Sections
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileTab("main")}
                    className={`py-2 rounded-lg text-center transition-all ${
                      mobileTab === "main"
                        ? "bg-[#FF5A1F] text-white shadow-sm"
                        : "text-neutral-700 hover:text-black"
                    }`}
                  >
                    Main Website Menu
                  </button>
                </div>
              )}

              {isNorbitPage && (
                <div className="grid grid-cols-2 p-1 bg-black/5 rounded-xl mb-1.5 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setMobileTab("norbit")}
                    className={`py-2 rounded-lg text-center transition-all ${
                      mobileTab === "norbit"
                        ? "bg-[#FF5A1F] text-white shadow-sm"
                        : "text-neutral-700 hover:text-black"
                    }`}
                  >
                    nORBIT Sections
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileTab("main")}
                    className={`py-2 rounded-lg text-center transition-all ${
                      mobileTab === "main"
                        ? "bg-[#FF5A1F] text-white shadow-sm"
                        : "text-neutral-700 hover:text-black"
                    }`}
                  >
                    Main Website Menu
                  </button>
                </div>
              )}

              {isGuardianPage && mobileTab === "guardian" ? (
                <>
                  <div className="px-3.5 py-1.5 mb-1 flex items-center justify-between border-b border-black/5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Jump to Section</span>
                    <span className="text-[10px] font-mono text-[#FF5A1F]">● Live</span>
                  </div>
                  {GUARDIAN_NAV.map((l) => {
                    const isSecActive = activeGuardianSection === l.id;
                    return (
                      <a
                        key={l.label}
                        href={l.to}
                        onClick={() => setOpen(false)}
                        className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-between ${
                          isSecActive
                            ? "bg-[#FF5A1F]/10 text-[#FF5A1F] font-semibold"
                            : "text-neutral-800 hover:bg-black/5"
                        }`}
                      >
                        <span>{l.label}</span>
                        {isSecActive && <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />}
                      </a>
                    );
                  })}
                  <div className="pt-2 mt-2 border-t border-black/5">
                    <button
                      onClick={() => {
                        setOpen(false);
                        openConsultationModal("Guardian Security Audit");
                      }}
                      className="w-full py-3 rounded-full bg-[#FF5A1F] text-white text-xs font-semibold hover:bg-[#ff6d38] transition-colors flex items-center justify-center gap-1.5 shadow-md shadow-[#FF5A1F]/25"
                    >
                      <span>Book a Security Audit</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              ) : isNorbitPage && mobileTab === "norbit" ? (
                <>
                  <div className="px-3.5 py-1.5 mb-1 flex items-center justify-between border-b border-black/5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Jump to Section</span>
                    <span className="text-[10px] font-mono text-[#FF5A1F]">● Live</span>
                  </div>
                  {NORBIT_NAV.map((l) => {
                    const isSecActive = activeNorbitSection === l.id;
                    return (
                      <a
                        key={l.label}
                        href={l.to}
                        onClick={() => setOpen(false)}
                        className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-between ${
                          isSecActive
                            ? "bg-[#FF5A1F]/10 text-[#FF5A1F] font-semibold"
                            : "text-neutral-800 hover:bg-black/5"
                        }`}
                      >
                        <span>{l.label}</span>
                        {isSecActive && <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />}
                      </a>
                    );
                  })}
                  <div className="pt-2 mt-2 border-t border-black/5">
                    <button
                      onClick={() => {
                        setOpen(false);
                        openConsultationModal("nORBIT Enterprise OS Demo");
                      }}
                      className="w-full py-3 rounded-full bg-[#FF5A1F] text-white text-xs font-semibold hover:bg-[#ff6d38] transition-colors flex items-center justify-center gap-1.5 shadow-md shadow-[#FF5A1F]/25"
                    >
                      <span>Request nORBIT Access</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {NAV.map((l) => {
                    if (l.hasDropdown) {
                      return (
                        <div key={l.to} className="flex flex-col">
                          <div className="flex items-center justify-between">
                            <Link
                              to={l.to}
                              onClick={() => setOpen(false)}
                              className={`flex-1 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                                isActive(l.to) ? "text-[#FF5A1F] bg-[#FF5A1F]/10 font-semibold" : "text-neutral-800 hover:bg-black/5"
                              }`}
                            >
                              {l.label}
                            </Link>
                            <button
                              type="button"
                              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                              className="p-2 rounded-lg text-neutral-600 hover:bg-black/5"
                              aria-label="Toggle services submenu"
                            >
                              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180 text-[#FF5A1F]" : ""}`} />
                            </button>
                          </div>

                          {mobileServicesOpen && (
                            <div className="pl-3 pr-1 py-1 grid grid-cols-1 gap-1 border-l-2 border-[#FF5A1F]/30 ml-3.5 my-1">
                              {SERVICES_DROPDOWN.map((item) => {
                                if (item.external) {
                                  return (
                                    <a
                                      key={item.to}
                                      href={item.to}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setOpen(false)}
                                      className="px-2.5 py-1.5 text-xs text-neutral-700 hover:text-[#FF5A1F] rounded-lg hover:bg-black/5 flex items-center justify-between"
                                    >
                                      <span>{item.label}</span>
                                      <span className="text-[10px] text-neutral-400">{item.desc}</span>
                                    </a>
                                  );
                                }
                                return (
                                  <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setOpen(false)}
                                    className="px-2.5 py-1.5 text-xs text-neutral-700 hover:text-[#FF5A1F] rounded-lg hover:bg-black/5 flex items-center justify-between"
                                  >
                                    <span>{item.label}</span>
                                    <span className="text-[10px] text-neutral-400">{item.desc}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setOpen(false)}
                        className={`px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive(l.to) ? "text-[#FF5A1F] bg-[#FF5A1F]/10 font-semibold" : "text-neutral-800 hover:bg-black/5"
                        }`}
                      >
                        {l.label}
                      </Link>
                    );
                  })}
                  <button
                    onClick={() => {
                      setOpen(false);
                      openConsultationModal();
                    }}
                    className="mt-2 px-4 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold text-center shadow-lg shadow-[#FF5A1F]/30"
                  >
                    Book a Free Consultation
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Navbar;
